import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import type { MiddlewareHandler } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'
import { createSystemController } from './system.controller.js'

/**
 * System Routes (Hono + OpenAPI)
 *
 * Manual endpoints for module/plugin/manifest/capabilities lookups.
 * Entity lists are served via computed entities separately.
 */
export function createSystemRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()
  const controller = createSystemController(ctx)

  const authMw = ctx.core.middleware['auth']
  if (!authMw) {
    throw new Error('Auth middleware is required for system routes')
  }
  const auth = authMw as unknown as MiddlewareHandler<NexusEnv>

  // Generic JSON response schema — response shapes are modeled fully later.
  const anyJson = z.unknown()
  const anyObject = z.object({}).passthrough()

  // GET /capabilities — public
  const capabilitiesRoute = createRoute({
    method: 'get',
    path: '/capabilities',
    tags: ['System'],
    summary: 'Get system capabilities',
    responses: {
      200: {
        description: 'Capabilities object (core + plugin-contributed)',
        content: { 'application/json': { schema: anyObject } }
      },
      500: {
        description: 'Failed to resolve capabilities',
        content: { 'application/json': { schema: anyObject } }
      }
    }
  })
  app.openapi(capabilitiesRoute, controller.getCapabilities as OpenApiHandler)

  // GET /plugins — auth
  const listPluginsRoute = createRoute({
    method: 'get',
    path: '/plugins',
    tags: ['System'],
    summary: 'List all plugins',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: {
        description: 'Paginated plugin list',
        content: {
          'application/json': {
            schema: z.object({
              items: z.array(anyObject),
              total: z.number(),
              page: z.number(),
              limit: z.number(),
              totalPages: z.number(),
              hasNext: z.boolean()
            })
          }
        }
      },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(listPluginsRoute, controller.listPlugins as OpenApiHandler)

  // GET /plugin?name=... — public
  const getPluginRoute = createRoute({
    method: 'get',
    path: '/plugin',
    tags: ['System'],
    summary: 'Get a plugin by name',
    request: {
      query: z.object({ name: z.string() })
    },
    responses: {
      200: {
        description: 'Plugin DTO',
        content: { 'application/json': { schema: anyObject } }
      },
      404: {
        description: 'Plugin not found',
        content: { 'application/json': { schema: anyObject } }
      }
    }
  })
  app.openapi(getPluginRoute, controller.getPlugin as OpenApiHandler)

  // GET /modules — auth
  const listModulesRoute = createRoute({
    method: 'get',
    path: '/modules',
    tags: ['System'],
    summary: 'List registered modules',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: {
      query: z.object({
        source: z.enum(['core', 'user', 'all']).optional(),
        sort: z.string().optional(),
        order: z.enum(['asc', 'desc']).optional(),
        filters: z.string().optional()
      })
    },
    responses: {
      200: {
        description: 'Paginated module list',
        content: {
          'application/json': {
            schema: z.object({
              items: z.array(anyObject),
              total: z.number(),
              page: z.number(),
              limit: z.number(),
              totalPages: z.number(),
              hasNext: z.boolean()
            })
          }
        }
      },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(listModulesRoute, controller.listModules as OpenApiHandler)

  // GET /modules/:name — auth
  const getModuleRoute = createRoute({
    method: 'get',
    path: '/modules/{name}',
    tags: ['System'],
    summary: 'Get a module by name',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: {
      params: z.object({ name: z.string() })
    },
    responses: {
      200: { description: 'Module DTO', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } },
      404: { description: 'Module not found', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(getModuleRoute, controller.getModule as OpenApiHandler)

  // GET /manifest — auth
  const manifestRoute = createRoute({
    method: 'get',
    path: '/manifest',
    tags: ['System'],
    summary: 'Get combined manifest (core + user)',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Combined manifest DTO', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(manifestRoute, controller.getManifest as OpenApiHandler)

  // GET /manifest/core — auth
  const coreManifestRoute = createRoute({
    method: 'get',
    path: '/manifest/core',
    tags: ['System'],
    summary: 'Get core manifest',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Core manifest DTO', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(coreManifestRoute, controller.getCoreManifest as OpenApiHandler)

  // GET /manifest/user — auth
  const userManifestRoute = createRoute({
    method: 'get',
    path: '/manifest/user',
    tags: ['System'],
    summary: 'Get user manifest (plugins + standalone modules)',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'User manifest DTO or null in dev', content: { 'application/json': { schema: anyJson } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(userManifestRoute, controller.getUserManifest as OpenApiHandler)

  return app
}
